import {
  CommonHelperFunctions,
  DashboardState,
  EAppSupportType,
  ECardinality,
  ECodeGenerationType,
  EDatasourceType,
  EEnumType,
  EProjectPublishMethod,
  EProjectType,
  EPropertyType,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  EUserAccessPart,
  IActionDefinitionDto,
  IActionFolder,
  IComponent,
  IComponentDto,
  IConfig,
  IDatabaseTableModel,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IKType as IKTypeDto,
  IKuikaTypeResolution,
  IPatchSafeActionFolder,
  INewExportVersion,
  IPatchSafeScreenFolder,
  IProject,
  IProjectDto,
  IProjectGenerateRequestDTO,
  IProjectGenerationResponseDTO,
  IProjectMeta,
  IProjectTempStyles,
  IScreenDesign,
  IScreenDesignDto,
  IScreenDesignInUseData,
  IScreenDesignInfo,
  IScreenDesignListDto,
  IScreenFolder,
  ISdAlternative,
  ISymbol,
  ITypeModel,
  IValidationsModel,
  IWhatsappBusinessTemplateModel,
  SpecialDIIds,
  ViewPart,
  WarningPanelType
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { AddOperation, Operation, Patch, generateJSONPatch } from "generate-json-patch";
import { Guid } from "guid-typescript";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import LocalStorageService from "../../../auth/local-storage-service";
import { FireBaseService } from "../../../firebase/firebase";
import { ApiManagementService } from "../../../services/api-management-service";
import codeGenSseService from "../../../services/codegen-sse-service";
import { ComponentService } from "../../../services/component-service";
import { ComponentSetService } from "../../../services/component-set-service";
import { ConfigService } from "../../../services/config-service";
import { DatasourceService } from "../../../services/datasource-service";
import { startDesignerLive } from "../../../services/designer-live/designer-live";
import { NotificationTemplateService } from "../../../services/notification-template-service";
import { shouldOpenSwaggerPreviewTab } from "../../../services/preview-api-tab-service";
import { normalizePreviewUrl } from "../../../services/preview-url-service";
import { ProjectDataTypesService } from "../../../services/project-data-types-service";
import { ProjectService } from "../../../services/project-service";
import { ScreenService } from "../../../services/screen-service";
import { WidgetProjectInfo, WidgetsService } from "../../../services/widgets-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { VALIDATION_RAN_EVENT } from "../../../shared/utility/issues/issues-model";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  graftClientProjectEnrichments,
  isProjectRefreshSafe
} from "../../../shared/utility/project-refresh-enrichment";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { StaticTableHelper } from "../../../shared/utility/static-table-helper";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

declare let window: any;
interface ITables {
  id: string;
  tables: Array<IDatabaseTableModel>;
  datasourceId: string;
}

interface ITable {
  id?: string;
  key: string;
  title: string;
  columns: any;
  dropped: boolean;
}

interface IRunProjectDTO {
  url: string;
  urlBackend?: string;
}

export interface ProjectProviderState {
  isProjectsLoading: boolean;
  isProjectsLoaded: boolean;
  projects: { id: string; name: string; schemaName: string }[];
  setProjects: (projects: { id: string; name: string; schemaName: string }[], cb?: () => void) => void;
  setProjectsLoadingStatus: (isProjectsLoading: boolean, isProjectsLoaded: boolean) => void;
  project: IProject;
  projectTempStyles: IProjectTempStyles;
  setProjectTempStyles: (projectTempStyles: IProjectTempStyles) => void;
  prevProjectRevision: IProject;
  getProject: () => IProject;
  refreshProjectFromServer: (projectId: string) => Promise<void>;
  updateProject: (project: IProject, autosave?: boolean, changeReason?: ESdChangeReason) => Promise<void>;
  actionDefinitions: IActionDefinitionDto[];
  loadActionDefinitions: (dontFilterByProjectType?: boolean, skipEagerTableFetch?: boolean) => Promise<void>;
  processWithSpinner: <T>(delegate: () => Promise<T>) => Promise<T>;
  changeScreenListOrder: (screenList: IScreenDesignListDto[], a: any, b: any) => void;
  isActionsLoaded: boolean;
  types: IKTypeDto[];
  isProjectLoaded: boolean;
  screenList: IScreenDesignInfo[];
  validationErrors: IValidationsModel[];
  datasourceValidationErrors: string[];
  setDatasourceValidationErrors: (datasourceValidationErrors: string[]) => void;
  codeGenerationException: null | string;
  /** Standalone Issues-panel validation currently on the wire (drives its "Validating…" row). */
  isProjectValidationRunning: boolean;
  /**
   * Run the standalone project validation (Issues panel validate-on-open) and land the
   * result in `validationErrors`. Single-flight: concurrent calls join the same request;
   * a no-op while a codegen generation is active (it delivers fresher validations itself).
   * Deliberately NEVER aborted — see ProjectService.RunProjectValidation.
   */
  requestIssuesValidation: () => Promise<void>;
  isAlternativeSdModalActive: boolean;
  occupiedScreenName?: string;
  currentScreenOccupier?: string;
  setIsAlternativeSdModalActive: (isActive: boolean) => void;
  alternativeSdList: ISdAlternative[];
  setCodeGenerationTypeAndPublish: (
    codeGenerationType: ECodeGenerationType,
    configToBePublished: IConfig,
    selectedPublishTypes: string,
    shouldBackup?: boolean,
    shouldTriggerCloudDeployment?: boolean,
    includeMigrationArtifactsOnPublish?: boolean
  ) => void;
  cancelCurrentGeneration: () => void;
  runApp: () => Promise<IRunProjectDTO | undefined>;
  showExecutablesSentMessage: boolean;
  handleExecutablesSentMessageVisibility: (value: boolean) => void;
  // Export just minted these workflow versions; the designer offers an optional name/description prompt.
  newExportVersionsToDetail: INewExportVersion[];
  // True when that prompt must also carry the "executables will arrive by email" notice —
  // the version modal replaces the publish popover's sent-message so the two never compete for focus (KOA-18257).
  newExportVersionsMailInfo: boolean;
  clearNewExportVersionsToDetail: () => void;
  generationProcessTimer: number;
  isNewScreenModalVisible: boolean;
  setNewScreenModalVisibility: (value: boolean) => void;
  isNewFolderModalVisible: boolean;
  setNewFolderModalVisibility: (value: boolean) => void;
  /**
   * Folder a screen/folder opened through the shared modal flags should be created in.
   * The screens panel targets folders through its own local state; callers outside it
   * (the row-2 screen selector's per-folder "+") have no way in, so they set this and
   * the panel consumes it when the modal opens. Null means "project root".
   */
  pendingScreenFolderTargetId: string | null;
  setPendingScreenFolderTargetId: (folderId: string | null) => void;
  /**
   * Screen type a create opened through the shared modal flags should produce. Same reason as the
   * folder target above: the screens panel picks the type from its own local state, so a caller
   * outside it (the row-2 selector's Master "+") sets this instead. Null means the panel's default.
   */
  pendingNewScreenType: EScreenType | null;
  setPendingNewScreenType: (screenType: EScreenType | null) => void;
  isGeneratingPreview: boolean;
  handleSetPreviewGenerationStatus: (isGeneratingPreview: boolean) => void;
  searchTexts: string[];
  setSearchTexts: (panelKey: ViewPart, searchText: string) => void;
  showAccessLimitedModal: boolean;
  handleChangeVisibilityOfAccessLimitedModal: (showAccessLimitedModal?: boolean) => void;
  isProcessingWithSpinner: boolean;
  fetchWhatsappBusinessTemplates: () => void;
  whatsappBusinessTemplates: IWhatsappBusinessTemplateModel[];
  widgetDomainTypes: Map<string, ITypeModel>;
  getWidgetDomainType: (domainTypeName: string) => ITypeModel | undefined;
}

type ArrayToObject<T> = T extends Array<infer U> ? { [key: string]: U } : T;

// Recursively apply ArrayToObject to properties of T
type JsonPatchSafe<T> = {
  [K in keyof T]: ArrayToObject<T[K]>;
};

export class ProjectProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ProjectProviderState
> {
  private schemas: Map<string, ITables> = new Map();

  private widgetDomainTypesMap: Map<string, ITypeModel> = new Map();

  static defaultState: ProjectProviderState = {
    projects: [],
    isProjectsLoaded: false,
    isProjectsLoading: false,
    setProjects: () => {},
    setProjectsLoadingStatus: () => {},
    project: {} as IProject,
    projectTempStyles: {} as IProjectTempStyles,
    setProjectTempStyles: () => {},
    prevProjectRevision: {} as IProject,
    getProject: () => {
      return {} as IProject;
    },
    isProjectLoaded: false,
    refreshProjectFromServer: () => new Promise(() => {}),
    updateProject: () => new Promise(() => {}),
    actionDefinitions: [],
    loadActionDefinitions: () => new Promise(() => {}),
    // Placeholder until the real partition mounts: run the work, just without a spinner.
    processWithSpinner: <T,>(delegate: () => Promise<T>): Promise<T> => delegate(),
    changeScreenListOrder: () => {},
    isActionsLoaded: false,
    types: [],
    screenList: [],
    validationErrors: [],
    datasourceValidationErrors: [],
    setDatasourceValidationErrors: () => {},
    codeGenerationException: null,
    isProjectValidationRunning: false,
    requestIssuesValidation: async () => {},
    isAlternativeSdModalActive: false,
    currentScreenOccupier: undefined,
    occupiedScreenName: undefined,
    setIsAlternativeSdModalActive: () => {},
    alternativeSdList: [],
    setCodeGenerationTypeAndPublish: () => {},
    cancelCurrentGeneration: () => {},
    runApp: async () => undefined,
    showExecutablesSentMessage: false,
    handleExecutablesSentMessageVisibility: () => {},
    newExportVersionsToDetail: [],
    newExportVersionsMailInfo: false,
    clearNewExportVersionsToDetail: () => {},
    generationProcessTimer: 0,
    isNewScreenModalVisible: false,
    setNewScreenModalVisibility: () => {},
    isNewFolderModalVisible: false,
    setNewFolderModalVisibility: () => {},
    pendingScreenFolderTargetId: null,
    setPendingScreenFolderTargetId: () => {},
    pendingNewScreenType: null,
    setPendingNewScreenType: () => {},
    isGeneratingPreview: false,
    handleSetPreviewGenerationStatus: () => {},
    searchTexts: [],
    setSearchTexts: () => {},
    isProcessingWithSpinner: false,
    showAccessLimitedModal: false,
    handleChangeVisibilityOfAccessLimitedModal: () => {},
    fetchWhatsappBusinessTemplates: () => {},
    whatsappBusinessTemplates: [],
    widgetDomainTypes: new Map<string, ITypeModel>(),
    getWidgetDomainType: () => undefined
  };

  getProviderValues = (): ProjectProviderState => {
    return {
      projects: this.state.projects,
      isProjectsLoading: this.state.isProjectsLoading,
      isProjectsLoaded: this.state.isProjectsLoaded,
      setProjects: this.setProjects,
      setProjectsLoadingStatus: this.setProjectsLoadingStatus,
      prevProjectRevision: this.state.prevProjectRevision,
      project: this.state.project,
      projectTempStyles: this.state.projectTempStyles,
      setProjectTempStyles: this.setProjectTempStyles,
      getProject: this.getProject,
      isProjectLoaded: this.state.isProjectLoaded,
      refreshProjectFromServer: this.refreshProjectFromServer,
      updateProject: this.updateProject,
      actionDefinitions: this.state.actionDefinitions,
      loadActionDefinitions: this.loadActionDefinitions,
      processWithSpinner: this.processWithSpinner,
      changeScreenListOrder: this.changeScreenListOrder,
      isActionsLoaded: this.state.isActionsLoaded,
      types: this.state.types,
      screenList: this.state.screenList,
      validationErrors: this.state.validationErrors,
      datasourceValidationErrors: this.state.datasourceValidationErrors,
      setDatasourceValidationErrors: this.setDatasourceValidationErrors,
      codeGenerationException: this.state.codeGenerationException,
      isProjectValidationRunning: this.state.isProjectValidationRunning,
      requestIssuesValidation: this.requestIssuesValidation,
      isAlternativeSdModalActive: this.state.isAlternativeSdModalActive,
      currentScreenOccupier: this.state.currentScreenOccupier,
      occupiedScreenName: this.state.occupiedScreenName,
      setIsAlternativeSdModalActive: this.setIsAlternativeSdModalActive,
      alternativeSdList: this.state.alternativeSdList,
      setCodeGenerationTypeAndPublish: this.setCodeGenerationTypeAndPublish,
      cancelCurrentGeneration: this.cancelCurrentGeneration,
      runApp: this.runApp,
      showExecutablesSentMessage: this.state.showExecutablesSentMessage,
      handleExecutablesSentMessageVisibility: this.handleExecutablesSentMessageVisibility,
      newExportVersionsToDetail: this.state.newExportVersionsToDetail,
      newExportVersionsMailInfo: this.state.newExportVersionsMailInfo,
      clearNewExportVersionsToDetail: this.clearNewExportVersionsToDetail,
      generationProcessTimer: this.state.generationProcessTimer,
      isNewScreenModalVisible: this.state.isNewScreenModalVisible,
      setNewScreenModalVisibility: this.setNewScreenModalVisibility,
      isNewFolderModalVisible: this.state.isNewFolderModalVisible,
      setNewFolderModalVisibility: this.setNewFolderModalVisibility,
      // Normalised: the provider's state is populated lazily, so this key is undefined
      // until something sets it — consumers should only ever see a folder id or null.
      pendingScreenFolderTargetId: this.state.pendingScreenFolderTargetId ?? null,
      setPendingScreenFolderTargetId: this.setPendingScreenFolderTargetId,
      // Normalised for the same reason as the folder target: undefined until something sets it.
      pendingNewScreenType: this.state.pendingNewScreenType ?? null,
      setPendingNewScreenType: this.setPendingNewScreenType,
      isGeneratingPreview: this.state.isGeneratingPreview,
      handleSetPreviewGenerationStatus: this.handleSetPreviewGenerationStatus,
      searchTexts: this.state.searchTexts,
      setSearchTexts: this.setSearchTexts,
      isProcessingWithSpinner: this.state.isProcessingWithSpinner,
      showAccessLimitedModal: this.state.showAccessLimitedModal,
      handleChangeVisibilityOfAccessLimitedModal: this.handleChangeVisibilityOfAccessLimitedModal,
      fetchWhatsappBusinessTemplates: this.fetchWhatsappBusinessTemplates,
      whatsappBusinessTemplates: this.state.whatsappBusinessTemplates,
      widgetDomainTypes: this.widgetDomainTypesMap,
      getWidgetDomainType: this.getWidgetDomainType
    };
  };

  getWidgetDomainType = (domainTypeName: string): ITypeModel | undefined => {
    return this.widgetDomainTypesMap.get(domainTypeName);
  };

  registerWidgetDomainTypes = (widgetProjects: WidgetProjectInfo[]): void => {
    // Clear existing widget domain types and re-register from all widget projects
    this.widgetDomainTypesMap.clear();

    for (const project of widgetProjects) {
      for (const component of project.components) {
        if (component.domainTypes) {
          for (const domainType of component.domainTypes) {
            // Domain types are already prefixed with project ID by the backend
            this.widgetDomainTypesMap.set(domainType.name, domainType);
          }
        }
      }
    }
  };

  setIsAlternativeSdModalActive = (isAlternativeSdModalActive: boolean) => {
    this.setState({ isAlternativeSdModalActive });
  };

  setProjects = (projects: { id: string; name: string }[], cb?: () => void) => {
    this.setState({ projects }, cb);

    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (activeCanvasIFrame && activeCanvasIFrame.contentWindow) {
      if (!(activeCanvasIFrame.contentWindow as any).kuika) {
        (activeCanvasIFrame.contentWindow as any).kuika = {};
      }
      (activeCanvasIFrame.contentWindow as any).kuika.projectsData = projects.reduce(
        (acc, project) => {
          acc[project.id] = { id: project.id, name: project.name };
          return acc;
        },
        {} as Record<string, { id: string; name: string }>
      );
    }
  };

  setProjectsLoadingStatus = (isProjectsLoading: boolean, isProjectsLoaded: boolean) => {
    this.setState({ isProjectsLoading, isProjectsLoaded });
  };

  checkForMicroAppsAndLoadScreens = async (screenDesign: IScreenDesign) => {
    if (!screenDesign?.displayItems) return;

    const microApp = this.findMicroAppComponent(screenDesign.displayItems);

    if (microApp) {
      const projectIdProp = microApp.item?.properties?.find((p) => p.name === "projectId");
      const projectId = projectIdProp?.symbol?.fixedValue;

      if (projectId && typeof projectId === "string") {
        try {
          const res = await ScreenService.GetScreenNamesByProjectId(projectId);

          if (res?.status === 200 && res.data) {
            const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
            if (activeCanvasIFrame && activeCanvasIFrame.contentWindow) {
              if (!(activeCanvasIFrame.contentWindow as any).kuika) {
                (activeCanvasIFrame.contentWindow as any).kuika = {};
              }
              if (!(activeCanvasIFrame.contentWindow as any).kuika.screensData) {
                (activeCanvasIFrame.contentWindow as any).kuika.screensData = {};
              }

              (activeCanvasIFrame.contentWindow as any).kuika.screensData[projectId] = res.data.reduce(
                (acc, screen) => {
                  acc[screen.id] = { id: screen.id, name: screen.name };
                  return acc;
                },
                {} as Record<string, { id: string; name: string }>
              );
            }
          }
        } catch (e) {
          console.warn(`Failed to load screens for MicroApp project ${projectId}:`, e);
        }
      }
    }
  };

  findMicroAppComponent = (displayItems: IDisplayItem[]): IDisplayItem | undefined => {
    const queue: IDisplayItem[] = [...displayItems];

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (current.item?.name === "MicroApp") {
        return current;
      }

      if (current.children?.length > 0) {
        queue.push(...current.children);
      }
    }

    return undefined;
  };

  setCodeGenerationTypeAndPublish = async (
    codeGenerationType: ECodeGenerationType,
    configToBePublished: IConfig,
    selectedPublishTypes: string,
    shouldBackup?: boolean,
    _shouldTriggerCloudDeployment?: boolean,
    includeMigrationArtifactsOnPublish?: boolean
  ) => {
    await this.partial.screenProvider.saveScreen(true);
    await this.previewApp(
      codeGenerationType,
      configToBePublished,
      selectedPublishTypes,
      shouldBackup,
      includeMigrationArtifactsOnPublish
    );
    const project = _.cloneDeep(this.state.project);
    project.lastRunConfigId = configToBePublished.id;
    this.updateProject(project);
  };

  cancelCurrentGeneration = async () => {
    await codeGenSseService.cancelGeneration();
    this.setSpinnerOff();
    // The cancelled run will never deliver validations — release the Issues pending
    // indicator whoever currently owns it.
    this.clearValidationPending();
    this.setState({ isGeneratingPreview: false });
    codeGenSseService.removeAllListeners();
    codeGenSseService.stopConnection();
  };

  /** In-flight standalone validation — reopening the Issues popover joins it (never restarts). */
  private issuesValidationInFlight: Promise<void> | null = null;

  /**
   * Monotonic token over "a validation result is pending" — what the Issues button's spinner
   * badge and skeleton show. Owned by whichever source bumped it LAST: a generation that
   * starts while a standalone Issues run is on the wire takes the token over, the
   * standalone's late response is DROPPED (it predates the generation's state), and the
   * spinner holds until the generation's own validations land (or it errors/cancels).
   */
  private validationPendingEpoch: number = 0;

  /** A validation source started; returns the epoch that source must present to end pending. */
  private beginValidationPending = (): number => {
    this.validationPendingEpoch += 1;
    if (!this.state.isProjectValidationRunning) this.setState({ isProjectValidationRunning: true });
    return this.validationPendingEpoch;
  };

  /** End pending — only honored while `epoch` is still the owner (a newer source may have taken over). */
  private endValidationPending = (epoch: number): void => {
    if (epoch !== this.validationPendingEpoch) return;
    if (this.state.isProjectValidationRunning) this.setState({ isProjectValidationRunning: false });
  };

  /** Unconditional clear (user cancelled the generation): orphans every pending source. */
  private clearValidationPending = (): void => {
    this.validationPendingEpoch += 1;
    if (this.state.isProjectValidationRunning) this.setState({ isProjectValidationRunning: false });
  };

  requestIssuesValidation = (): Promise<void> => {
    if (this.issuesValidationInFlight) return this.issuesValidationInFlight;
    // A generation is running or streaming — it validates the project itself and lands the
    // result in the same state; a standalone run now would only double the engine load.
    if (codeGenSseService.isGenerationActive()) return Promise.resolve();
    // Same reasoning for an AGENT BUILD. Every mutation it makes marks the last validation
    // stale (the dirty interceptor watches the process/screen/datasource routes it writes
    // to), so opening the Issues panel mid-build starts a full validation — and the build's
    // own verify edge FORCES another one at the end, which by contract never joins a run
    // that started earlier. Two ~18s (worst case 83s) runs, both fanning out to customer
    // datasources, for one answer. Deferring costs nothing: the build's forced run lands in
    // this same state and the panel picks it up.
    if (this.partial.collabProvider.isAgentBuildInProgress()) return Promise.resolve();

    const epoch: number = this.beginValidationPending();
    this.issuesValidationInFlight = ProjectService.RunProjectValidation()
      .then((res) => {
        // A generation started while this was on the wire: its state supersedes ours — drop
        // the result and leave the pending indicator to the generation.
        if (epoch !== this.validationPendingEpoch) return;
        if (res?.status !== 200 || !res.data) return;
        // Same landing as a generation run's validations. codeGenerationException is NOT
        // touched — that's generation-scoped info this run knows nothing about.
        this.setState({ validationErrors: res.data.errors ?? [] }, () => {
          window.dispatchEvent(new Event(VALIDATION_RAN_EVENT));
        });
      })
      .catch(() => {
        // Errors are already surfaced by the Axios interceptor; a failed background
        // validation must not break the popover.
      })
      .finally(() => {
        this.issuesValidationInFlight = null;
        this.endValidationPending(epoch);
      });
    return this.issuesValidationInFlight;
  };

  clearNewExportVersionsToDetail = () => {
    this.setState({ newExportVersionsToDetail: [], newExportVersionsMailInfo: false });
  };

  handleExecutablesSentMessageVisibility = (value: boolean) => {
    this.setState({ showExecutablesSentMessage: value });
  };

  // TODO: EMRE - rename function name -> publishApp
  previewApp = async (
    codeGenerationType: ECodeGenerationType = ECodeGenerationType.Publish,
    selectedConfig: IConfig,
    selectedPublishTypes: string,
    shouldBackup?: boolean,
    includeMigrationArtifactsOnPublish?: boolean
  ) => {
    this.setSpinnerOn();

    this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Close);
    this.setState({ validationErrors: [], codeGenerationException: null });
    // The generation validates the project as part of the run — the Issues button shows its
    // pending indicator until this run's validations land (superseding any standalone run).
    const validationEpoch: number = this.beginValidationPending();
    const sd = this.partial.screenProvider.getSerializableScreenDesign();
    const screenDTO = {} as IScreenDesignDto;
    screenDTO.json = jsonStringifySafe(sd);
    screenDTO.name = sd.name;
    screenDTO.id = sd.id;
    try {
      const obj = {
        projectId: this.state.project.id,
        selectedConfId: selectedConfig.id,
        CodeGenerationType: codeGenerationType,
        currentSdData: screenDTO,
        selectedPublishTypes,
        IncludeMigrationArtifactsOnPublish: includeMigrationArtifactsOnPublish ?? false
      } as IProjectGenerateRequestDTO;

      ProjectService.GenerateProjectViaSse(obj);

      codeGenSseService.onMessage("SetSpinnerOff", (message) => {
        this.setSpinnerOff();
      });

      codeGenSseService.onMessage("GenerationCancelled", () => {
        this.setSpinnerOff();
        this.endValidationPending(validationEpoch);
        codeGenSseService.removeAllListeners();
        codeGenSseService.stopConnection();
      });

      // The dedicated errorOccuredCallback slot belongs to RunButton; this named-event
      // listener rides the same dispatch without stealing it. A failed generation delivers
      // no validations — the pending indicator must not hang on the spinner forever.
      codeGenSseService.onMessage("ErrorOccured", () => {
        this.endValidationPending(validationEpoch);
      });

      codeGenSseService.onMessage("ExecutePreview", async (message) => {
        const promiseResponse = new Promise<AxiosResponse<IProjectGenerationResponseDTO, any>>((resolve) => {
          resolve(JSON.parse(message));
        });
        await this.executePreview(
          promiseResponse,
          codeGenerationType,
          selectedConfig,
          selectedPublishTypes,
          shouldBackup
        );
        this.endValidationPending(validationEpoch);
      });
    } catch (error: any) {
      this.endValidationPending(validationEpoch);
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ generationProcessTimer: 100 });
    }
  };

  executePreview = async (
    promiseResponse: any,
    codeGenerationType: ECodeGenerationType,
    selectedConfig: IConfig,
    selectedPublishTypes: string,
    shouldBackup?: boolean
  ) => {
    const promises = [promiseResponse];

    if (
      shouldBackup &&
      (codeGenerationType === ECodeGenerationType.DownloadSourceCode || ECodeGenerationType.DownloadExecutables)
    ) {
      const timezoneOffset = new Date().getTimezoneOffset() * 60000;
      const newProjectName = `${this.state.project.name}_${new Date(Date.now() - timezoneOffset)
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .slice(0, -5)}`;

      ProjectService.Duplicate({
        projectIdToDuplicate: this.state.project.id,
        newProjectName,
        isBackup: true
      });
    }

    const allSettledResponse = await Promise.allSettled(promises);

    if (allSettledResponse[0].status === "fulfilled") {
      const res = allSettledResponse[0].value;
      if (!res) return;
      if (res.generatingAnAppInSameDomain) {
        codeGenSseService.stopConnection();
        this.setSpinnerOff();
        KMainFunctions.displayWarningNotification(
          `There is already an app generation on progress [${res.generatingAnAppInSameDomain.projectName}] by [${res.generatingAnAppInSameDomain.generatingBy}]. Please try again later.`
        );
        return;
      }

      // A successful export may have minted new workflow versions. The optional name/description
      // prompt then doubles as the export-status surface: it replaces the publish popover's
      // "executables sent" message (and carries the mail notice itself) so the two surfaces
      // never compete for focus (KOA-18257).
      const isExport =
        codeGenerationType === ECodeGenerationType.DownloadSourceCode ||
        codeGenerationType === ECodeGenerationType.DownloadExecutables;
      const exportHasErrors =
        (res.codeGenerationException?.length ?? 0) > 0 || (res.validations?.some((x) => x.type === "Error") ?? false);
      const willShowVersionModal: boolean = isExport && !exportHasErrors && (res.newExportVersions?.length ?? 0) > 0;
      // Mirrors the branch below that shows the popover message: executables export with a
      // completely clean run (no validations of any severity, no codegen exceptions).
      const executablesMailApplies: boolean =
        codeGenerationType === ECodeGenerationType.DownloadExecutables &&
        (res.validations?.length ?? 0) === 0 &&
        (res.codeGenerationException?.length ?? 0) === 0;

      this.setState(
        {
          validationErrors: res.validations,
          codeGenerationException: res.codeGenerationException
        },
        () => {
          // A run just validated the current screen — the Issues "all-clear ✓" may now show.
          window.dispatchEvent(new Event(VALIDATION_RAN_EVENT));
          const hasValidations = res.validations?.length > 0;
          const hasCodeGenErrors = res.codeGenerationException?.length > 0;
          const hasValidationErrors = res.validations?.some((x) => x.type === "Error") ?? false;

          if (hasValidations || hasCodeGenErrors) {
            if (hasCodeGenErrors || hasValidationErrors) {
              const firebase = new FireBaseService();
              firebase.logEvent("preview_error", {
                validation_error: hasValidationErrors,
                code_generation_error: hasCodeGenErrors,
                project_id: this.state.project.id,
                project_name: this.state.project.name,
                selected_configuration: this.state.project.selectedConfigurationId,
                code_generation_message: hasCodeGenErrors ? res.codeGenerationException.length : ""
              });
            }

            codeGenSseService.stopConnection();
            this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Open);
          } else if (!hasValidationErrors && !hasCodeGenErrors) {
            if (!hasValidations && !hasCodeGenErrors) {
              this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Close);
            }

            if (codeGenerationType === ECodeGenerationType.DownloadExecutables) {
              if (!willShowVersionModal) this.handleExecutablesSentMessageVisibility(true);
              codeGenSseService.stopConnection();
            }
          }
        }
      );

      // Canonicalize before opening: the slash-less form 301s to a
      // scheme-downgraded http URL that Safari follows onto a port that resets
      // the connection (KOA-17913).
      const url = normalizePreviewUrl(res.frontEndUrl);
      // KOA-18168: swagger'i ACIKCA hedefle. Onceden buraya ciplak backendUrl
      // geliyordu ve "/swagger" ekini NGINX koyuyordu: systemd preview'inda
      // `location = /{proj}-api` ve `location /{proj}-api/swagger` bloklari
      // path-based URL'i flat-host'un /swagger'ina 301'liyordu
      // (NginxConfigurator.cs:613-625), ayrica flat-host kokunde de
      // `location = /` -> /swagger rewrite'i vardi (:386-388). K8s preview'da
      // dispatcher paylasimli ve generic; bu iki kurali tasiyamiyor (flat host
      // {ws}-{env} tek label ve ikisi de tireli oldugu icin AYRISTIRILAMIYOR --
      // preview-static-nginx-conf bunu kendi yorumunda da belirtiyor). Sonuc:
      // sekme backend kokunde acilip "Welcome to Kuika.App" gosteriyordu.
      // Eki burada koyuyoruz; dispatcher'a dokunmak PreWarm'i (KubernetesRuntime
      // Provisioner.cs, https://{apiHost}/ ile KEDA scale-from-zero tetikleme)
      // 302'ye dusurup cold-start'i geri getirirdi.
      // normalizePreviewUrl uzantisiz path'e sondaki slash'i ekler, o yuzden
      // birlestirme "host/" + "swagger/index.html" olur. Bos/undefined backendUrl'de
      // "undefinedswagger/..." uretmemek icin oldugu gibi birakilir.
      const backendBase: string = normalizePreviewUrl(res.backendUrl);
      const urlBackend: string = backendBase ? `${backendBase}swagger/index.html` : backendBase;

      if (
        window.location.hostname !== "localhost" &&
        res.clientPort &&
        res.clientPort > 0 &&
        selectedConfig.isRunConfig &&
        codeGenerationType !== ECodeGenerationType.DownloadExecutables &&
        codeGenerationType !== ECodeGenerationType.DownloadSourceCode
      ) {
        // KOA-18175: resolve the Swagger-tab decision against the server while
        // the pre-open delay runs — project.apiManagements is lazily loaded and
        // may be stale when the API Builder section was never opened.
        const project: IProject = this.partial.projectProvider.getProject();
        const swaggerTabCheck: Promise<boolean> = shouldOpenSwaggerPreviewTab(project?.id, project?.apiManagements);
        await this.delay(3000);
        const newTab = window.open(url, "_blank");
        const shouldOpenSwaggerTab: boolean = await swaggerTabCheck;
        if (shouldOpenSwaggerTab) {
          window.open(urlBackend, "_blank");
        }
        if (newTab == null || typeof newTab === "undefined") {
          alert("Please disable your pop-up blocker.");
        }
      }

      if (
        res.generatedAppFiles &&
        res.generatedAppFiles.length > 0 &&
        codeGenerationType !== ECodeGenerationType.DownloadExecutables
      ) {
        const cgApiUrlResponse = await ApiManagementService.GetCodeGenerationApiUrl();
        const cgApiUrl = cgApiUrlResponse.data;
        res.generatedAppFiles.forEach(async (file) => {
          const subtracted = file.substring(file.indexOf("/v1/api"));
          const finished = cgApiUrl + subtracted;
          await this.delay(3000);
          const newTab = window.open(finished, "_blank");

          if (newTab == null || typeof newTab === "undefined") {
            alert("Please disable your pop-up blocker.");
          }
        });
      }

      if (willShowVersionModal) {
        this.setState({
          newExportVersionsToDetail: res.newExportVersions,
          newExportVersionsMailInfo: executablesMailApplies
        });
      }
    }
  };

  runApp = async (): Promise<IRunProjectDTO | undefined> => {
    this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Close);
    this.setState({ validationErrors: [], codeGenerationException: null });
    // Same contract as previewApp: this run owns the Issues pending indicator until its
    // validations land (the finally below covers every early return and the catch).
    const validationEpoch: number = this.beginValidationPending();
    const sd = this.partial.screenProvider.getSerializableScreenDesign();
    const screenDTO = {} as IScreenDesignDto;
    screenDTO.json = jsonStringifySafe(sd);
    screenDTO.name = sd.name;
    screenDTO.id = sd.id;
    try {
      const selectedConfig = this.partial.configManagerProvider.state.configurations.find((conf) => conf.isRunConfig);
      const obj = {
        projectId: this.state.project.id,
        selectedConfId: selectedConfig?.id,
        CodeGenerationType: ECodeGenerationType.Publish,
        currentSdData: screenDTO,
        selectedPublishTypes: `${EProjectPublishMethod.webUi}`
      } as IProjectGenerateRequestDTO;

      const res = await ProjectService.GenerateProject(obj);
      if (res && res.status === 200) {
        if (res.data.generatingAnAppInSameDomain) {
          KMainFunctions.displayWarningNotification(
            `There is already an app generation on progress [${res.data.generatingAnAppInSameDomain.projectName}] by [${res.data.generatingAnAppInSameDomain.generatingBy}]. Please try again later.`
          );
          return;
        }

        this.setState(
          {
            validationErrors: res.data.validations,
            codeGenerationException: res.data.codeGenerationException
          },
          () => {
            // A run just validated the current screen — the Issues "all-clear ✓" may now show.
            window.dispatchEvent(new Event(VALIDATION_RAN_EVENT));
            if (
              res.data?.validations?.length > 0 ||
              (res.data.codeGenerationException && res.data.codeGenerationException.length > 0)
            ) {
              const firebase = new FireBaseService();
              const hasValidationErr: boolean = (res.data?.validations?.length ?? 0) > 0;
              const codeGenExceptionCount: number = res.data.codeGenerationException?.length ?? 0;
              const hasCodeGenErr: boolean = codeGenExceptionCount > 0;
              firebase.logEvent("preview_error", {
                validation_error: hasValidationErr,
                code_generation_error: hasCodeGenErr,
                project_id: this.state.project.id,
                project_name: this.state.project.name,
                selected_configuration: this.state.project.selectedConfigurationId,
                code_generation_message: hasCodeGenErr ? codeGenExceptionCount : ""
              });
              this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Open);
            } else if (res.data?.validations?.length === 0 && res.data.codeGenerationException === null) {
              this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Close);
            }
          }
        );

        // Validations are on state — release the Issues indicator now rather than after the
        // preview-URL handling below (which deliberately sleeps before opening the tab).
        this.endValidationPending(validationEpoch);

        let url = res.data?.frontEndUrl;
        let urlBackend = res.data?.backendUrl;

        if (
          // window.location.hostname !== "localhost" &&
          res.data?.clientPort &&
          res.data.clientPort > 0
        ) {
          if (window.location.hostname === "localhost") {
            const baseLocalURL = `${window.location.protocol}//localhost`;
            url = `${baseLocalURL}:${res.data.clientPort}`;

            if (res.data?.apiPort && res.data.apiPort > 0) {
              urlBackend = `${baseLocalURL}:${res.data.apiPort}`;
            }
          } else {
            // KOA-18168: legacy `${url}-api` (path-based) backend URL'i K8s preview'larında 404
            // veriyor -- dispatcher backend'i ayrı {ws}-{proj}-api subdomain'inden route ediyor,
            // path'ten degil. API'nin zaten dondugu backendUrl'i (flat-host) kullan.
            urlBackend = res.data?.backendUrl;
          }

          urlBackend = `${urlBackend}/swagger/index.html`;

          await this.delay(3000);

          return {
            url,
            urlBackend
          };
        }
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.endValidationPending(validationEpoch);
      this.setState({ generationProcessTimer: 100 });
    }
  };

  delay(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  getProject = (): IProject => {
    return this.state.project;
  };

  convertArrayToMap<T>(array: T[], key: keyof T | (keyof T)[]): { [key: string]: T } {
    return array.reduce(
      (acc, item) => {
        if (Array.isArray(key)) {
          const keyString = key.map((k) => item[k]).join(",");
          acc[keyString] = item;
        } else if (item[key] && !acc[item[key] as unknown as string]) {
          acc[item[key] as unknown as string] = item;
        }

        return acc;
      },
      {} as Record<string, T>
    );
  }

  convertToPatchSafeScreenFolder = (screenFolder: IScreenFolder): IPatchSafeScreenFolder => {
    const result: IPatchSafeScreenFolder = {
      id: screenFolder.id,
      name: screenFolder.name,
      screenIds: {},
      children: {},
      parentFolderId: screenFolder.parentFolderId
    };

    if (screenFolder.screenIds) {
      screenFolder.screenIds.forEach((screenId: string) => {
        result.screenIds ??= {};
        result.screenIds[screenId] = screenId;
      });
    }

    if (screenFolder.children) {
      screenFolder.children.forEach((child: IScreenFolder) => {
        result.children ??= {};
        result.children[child.id] = this.convertToPatchSafeScreenFolder(child);
      });
    }

    return result;
  };

  convertToPatchSafeActionFolder = (actionFolder: IActionFolder): IPatchSafeActionFolder => {
    const result: IPatchSafeActionFolder = {
      id: actionFolder.id,
      name: actionFolder.name,
      actionIds: {},
      children: {},
      parentFolderId: actionFolder.parentFolderId,
      dataSourceType: actionFolder.dataSourceType
    };

    if (actionFolder.actionIds) {
      actionFolder.actionIds.forEach((actionId: string) => {
        result.actionIds ??= {};
        result.actionIds[actionId] = actionId;
      });
    }

    if (actionFolder.children) {
      actionFolder.children.forEach((child: IActionFolder) => {
        result.children ??= {};
        result.children[child.id] = this.convertToPatchSafeActionFolder(child);
      });
    }

    return result;
  };

  // Role folders (optional model) – mirrors screen folders; safe for backward compatibility
  private convertToPatchSafeRoleFolder = (roleFolder: any): any => {
    const result: any = {
      id: roleFolder.id,
      name: roleFolder.name,
      roleIds: {},
      children: {},
      parentFolderId: roleFolder.parentFolderId
    };
    if (roleFolder.roleIds) {
      roleFolder.roleIds.forEach((roleId: string) => {
        result.roleIds ??= {};
        result.roleIds[roleId] = roleId;
      });
    }
    if (roleFolder.children) {
      roleFolder.children.forEach((child: any) => {
        result.children ??= {};
        result.children[child.id] = this.convertToPatchSafeRoleFolder(child);
      });
    }
    return result;
  };

  generateJsonPatchSafeProjectMeta = (project: IProjectMeta): JsonPatchSafe<IProjectMeta> => {
    const projectMeta = {} as JsonPatchSafe<IProjectMeta>;

    // Map non-array properties
    Object.keys(project).forEach((key) => {
      if (Array.isArray(project[key])) {
        return;
      }

      projectMeta[key] = project[key];
    });

    // Map array properties
    if (project.configurations) {
      projectMeta.configurations = this.convertArrayToMap(project.configurations, "id");
    }

    if (project.processStepScreenIds) {
      projectMeta.processStepScreenIds = this.convertArrayToMap(project.processStepScreenIds, ["processId", "stepId"]);
    }

    if (project.stylingTypography) {
      projectMeta.stylingTypography = this.convertArrayToMap(project.stylingTypography, "typoId");
    }

    if (project.stylingBorder) {
      projectMeta.stylingBorder = this.convertArrayToMap(project.stylingBorder, "borderId");
    }

    if (project.stylingShadow) {
      projectMeta.stylingShadow = this.convertArrayToMap(project.stylingShadow, "shadowId");
    }

    if (project.stylingPosition) {
      projectMeta.stylingPosition = this.convertArrayToMap(project.stylingPosition, "positionId");
    }

    if (project.stylingFill) {
      projectMeta.stylingFill = this.convertArrayToMap(project.stylingFill, "fillId");
    }

    if (project.stylingEffects) {
      projectMeta.stylingEffects = this.convertArrayToMap(project.stylingEffects, "effectsId");
    }

    if (project.roles) {
      projectMeta.roles = this.convertArrayToMap(project.roles, "id");
    }

    if (project.languages) {
      projectMeta.languages = this.convertArrayToMap(project.languages, "id");
    }

    if (project.screenFolders) {
      // Tech Debt: This is a temporary hack. Write a separate interface just like on the backend.
      // @ts-ignore
      projectMeta.screenFolders = this.convertArrayToMap(
        project.screenFolders.map(this.convertToPatchSafeScreenFolder),
        "id"
      );
    }

    if (project.actionFolders) {
      // Tech Debt: This is a temporary hack. Write a separate interface just like on the backend.
      // @ts-ignore
      projectMeta.actionFolders = this.convertArrayToMap(
        project.actionFolders.map(this.convertToPatchSafeActionFolder),
        "id"
      );
    }

    // New optional role folders – keep optional for backward compatibility
    if ((project as any).roleFolders) {
      // @ts-ignore
      (projectMeta as any).roleFolders = this.convertArrayToMap(
        // @ts-ignore
        (project as any).roleFolders.map(this.convertToPatchSafeRoleFolder),
        "id"
      );
    }

    if (project.passwordPolicies) {
      projectMeta.passwordPolicies = this.convertArrayToMap(project.passwordPolicies, "id");
    }

    if (project.smtpSettings) {
      projectMeta.smtpSettings = this.convertArrayToMap(project.smtpSettings, "id");
    }

    if (project.awsSettings) {
      projectMeta.awsSettings = this.convertArrayToMap(project.awsSettings, "id");
    }

    if (project.googleMapsSettings) {
      projectMeta.googleMapsSettings = this.convertArrayToMap(project.googleMapsSettings, "id");
    }

    if (project.appStoreConnectApiSettings) {
      projectMeta.appStoreConnectApiSettings = this.convertArrayToMap(project.appStoreConnectApiSettings, "id");
    }

    if (project.googlePlayApiSettings) {
      projectMeta.googlePlayApiSettings = this.convertArrayToMap(project.googlePlayApiSettings, "id");
    }

    if (project.inversCloudBoxxSettings) {
      projectMeta.inversCloudBoxxSettings = this.convertArrayToMap(project.inversCloudBoxxSettings, "id");
    }

    if (project.commonMobileSettings) {
      projectMeta.commonMobileSettings = this.convertArrayToMap(project.commonMobileSettings, "id");
    }

    if (project.firebaseSettings) {
      projectMeta.firebaseSettings = this.convertArrayToMap(project.firebaseSettings, "id");
    }

    if (project.pushNotificationSettings) {
      projectMeta.pushNotificationSettings = this.convertArrayToMap(project.pushNotificationSettings, "id");
    }

    if (project.searchServiceSettings) {
      projectMeta.searchServiceSettings = this.convertArrayToMap(project.searchServiceSettings, "id");
    }

    if (project.reportCanvasSizes) {
      projectMeta.reportCanvasSizes = this.convertArrayToMap(project.reportCanvasSizes, "id");
    }

    if (project.savedColors) {
      projectMeta.savedColors = this.convertArrayToMap(project.savedColors, "id");
    }

    if (project.screenCanvasSizes) {
      projectMeta.screenCanvasSizes = {
        ..._.omit(project.screenCanvasSizes, "screens")
      };
      // Tech Debt: This is a temporary hack. Write a separate interface just like on the backend.
      // @ts-ignore
      projectMeta.screenCanvasSizes.screens = project.screenCanvasSizes.screens
        ? this.convertArrayToMap(project.screenCanvasSizes.screens, "id")
        : [];
    }

    if (project.projectTranslations) {
      projectMeta.projectTranslations = this.convertArrayToMap(project.projectTranslations, "language");
    }

    return projectMeta;
  };

  createProjectMetaPatch = (project: IProject): Patch => {
    const prevProjectRevision = _.cloneDeep(this.state.prevProjectRevision);
    const currentRevision = _.cloneDeep(project);

    const prevProjectMeta = this.generateJsonPatchSafeProjectMeta(
      KMainFunctions.convertProjectInterfaceToProjectMeta(prevProjectRevision)
    );
    const currentProjectMeta = this.generateJsonPatchSafeProjectMeta(
      KMainFunctions.convertProjectInterfaceToProjectMeta(currentRevision)
    );

    // // @ts-ignore
    // delete prevProjectRevision.componentSetObj;
    // // @ts-ignore
    // delete currentRevision.componentSetObj;

    const patch: Patch = generateJSONPatch(prevProjectMeta, currentProjectMeta).filter((p) => {
      if (p.path.startsWith("/apiManagements")) return false;
      if (p.path.startsWith("/scheduledJobs")) return false;
      if (p.path.startsWith("/projectAccess")) return false;
      return true;
    });

    patch.forEach((p) => {
      if (p.op === "replace" && p.value === undefined) {
        // @ts-ignore
        p.op = "remove";
      }
    });

    // Backend's JSON Patch applier cannot navigate deep paths into startProcessTriggers
    // (a Dictionary<workflowId, Trigger[]>): an add/remove at /startProcessTriggers/{wfId}/...
    // fails with "target location not found" whenever the wfId key or array index doesn't yet
    // exist on the stored project. So we drop the granular per-trigger ops and replace them with a
    // single top-level `add` that overwrites the whole field with the current local value — the one
    // op the backend can always apply. Only emitted when the diff actually touched triggers, so
    // unrelated saves never ship (or overwrite) the field.
    const isStartProcessTriggerPath = (p: Operation): boolean =>
      p.path === "/startProcessTriggers" || p.path.startsWith("/startProcessTriggers/");

    if (patch.some(isStartProcessTriggerPath)) {
      const filtered = patch.filter((p) => !isStartProcessTriggerPath(p));
      const consolidatedOp: AddOperation = {
        op: "add",
        path: "/startProcessTriggers",
        value: project.startProcessTriggers ?? {}
      };
      filtered.push(consolidatedOp);
      return filtered;
    }

    return patch;
  };

  // updateProject = _.debounce((project: IProject, autosave: boolean = true, changeReason?: ESdChangeReason) => this.runUpdateProject(project, autosave, changeReason), 200);
  updateProject = async (
    project: IProject,
    autosave: boolean = true,
    changeReason?: ESdChangeReason
  ): Promise<void> => {
    if (autosave !== true) {
      this.processWithSpinner(async () => {
        await this.processUpdateProject(project, changeReason);
      });
    } else {
      await this.processUpdateProject(project, changeReason);
    }
  };

  setProjectTempStyles = (projectTempStyles: IProjectTempStyles): void => {
    this.setState({ projectTempStyles });
  };

  processUpdateProject = async (project: IProject, changeReason?: ESdChangeReason): Promise<void> => {
    if (!project?.id) {
      return;
    }

    const hasUserAccessEditProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProject,
      project.projectAccess
    );
    if (!hasUserAccessEditProject) {
      return;
    }

    const patch: Patch = this.createProjectMetaPatch(project);

    const response = await ProjectService.PatchJson(patch, project.id);

    if (response.status !== 200) {
      return;
    }

    this.setState(
      {
        prevProjectRevision: _.cloneDeep(project),
        project,
        changeReason
      },
      () => {
        if (changeReason === ESdChangeReason.ChangeProjectStyle) {
          this.partial.frameProvider.renderFrame(changeReason);
        }
      }
    );
  };

  loadActionDefinitions = async (dontFilterByProjectType?: boolean, skipEagerTableFetch?: boolean) => {
    await this.partial.datasourceProvider.refreshDatabases();
    if (!this.state.datasources) return;

    const stateVars: any = {};
    // KOA-17363 — keep the eager `getTables` + `refreshTables` path for
    // designer flows (prodhotfix behavior). MCP `reload_datasources` passes
    // `skipEagerTableFetch=true` and follows up with the slim
    // `refreshTableIds` lazy-load path so it doesn't trigger the multi-MB
    // body-timeout.
    if (!skipEagerTableFetch) {
      let datasourceIds: string[] = [];
      this.state.datasources.map((datasource) => {
        if (
          datasource.datasourceType != EDatasourceType.RestApi &&
          datasource.datasourceType != EDatasourceType.RestApiWOSwagger &&
          datasource.datasourceType != EDatasourceType.CSharpFunctions &&
          datasource.datasourceType != EDatasourceType.CustomConnectionStringProvider &&
          datasource.datasourceType != EDatasourceType.BleService &&
          datasource.datasourceType != EDatasourceType.AIActions &&
          datasource.datasourceType != EDatasourceType.AIAgent &&
          datasource.datasourceType != EDatasourceType.VisionAction &&
          datasource.datasourceType != EDatasourceType.SAP
        ) {
          this.partial.datasourceProvider.getTables(datasource.id);
          datasourceIds.push(datasource.id);
        }
      });
      this.partial.datasourceProvider.refreshTables(datasourceIds);
    }

    try {
      const res = await DatasourceService.GetActionsByDomain();
      if (res && res.status === 200) {
        let actions = res.data;
        if (typeof actions === "string") {
          actions = JSON.parse(actions);
        }
        const _types = actions.filter((x) => x.returnTypeModel).map((x) => x.returnTypeModel);
        const isWebScreen = this.partial.screenProvider.getIsWebScreen();
        actions = actions.filter((x) => {
          const { projectType } = this.state.project;
          if (
            x.appSupportType &&
            x.appSupportType === EAppSupportType.Web &&
            projectType !== EProjectType.Desktop &&
            !isWebScreen &&
            !dontFilterByProjectType
          ) {
            return false;
          }
          if (
            x.appSupportType &&
            x.appSupportType === EAppSupportType.Mobile &&
            projectType === EProjectType.Desktop &&
            !dontFilterByProjectType
          ) {
            return false;
          }
          return true;
        });
        stateVars.actionDefinitions = actions;
        stateVars.types = _types;
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }

    // Merge the user-managed Kuika Data Types catalog into `sd.types` so the symbol picker and the
    // WF-parameter type reference dropdown resolve catalog types by name. Isolated so a catalog
    // fetch failure never blocks action loading.
    try {
      const catalogRes = await ProjectDataTypesService.GetUserDataTypes();
      if (catalogRes && catalogRes.status === 200 && Array.isArray(catalogRes.data)) {
        const baseTypes: IKTypeDto[] = (stateVars.types as IKTypeDto[]) ?? [];
        const existingNames = new Set<string>(baseTypes.map((t) => t.name));
        // `sd.types` holds serialized `TypeModel`s (field `type` is a string, nesting via
        // `elementTypeModel`) — the exact shape `IKuikaDataType` already is. Push catalog entries RAW
        // so field navigation in the symbol picker works; converting to the IKType interface shape
        // (nested `type: IKType`) would make them inconsistent with the rest of `sd.types`.
        const catalogTypes: IKTypeDto[] = (catalogRes.data as unknown as IKTypeDto[]).filter(
          (t) => !existingNames.has(t.name)
        );
        stateVars.types = [...baseTypes, ...catalogTypes];
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }

    this.setState({ ...stateVars, changeReason: ESdChangeReason.OtherSetStates }, () => {
      if (!this.state.isActionsLoaded) this.setState({ isActionsLoaded: true });
    });
  };

  /**
   * Runs `delegate` with the spinner up, resolving with its result ONLY after it has
   * finished. The spinner-on commit is a React setState callback, so the delegate cannot
   * start until React committed — that callback has to be awaited, or the returned
   * promise resolves before the work even begins. It used to: `await processWithSpinner(…)`
   * was a no-op at all 13 awaiting call sites, and `saveScreen` returned the `true` it had
   * initialised `persisted` with while the save was still in flight — so an explicit save
   * that CoreApi rejected reported success and the follow-up reload discarded the tree
   * (KOA-18256). A delegate that throws now surfaces to the caller instead of dying as an
   * unhandled rejection inside React's callback.
   */
  // `<T,>` not `<T>`: in a .tsx file a bare type parameter parses as a JSX tag.
  processWithSpinner = async <T,>(delegate: () => Promise<T>): Promise<T> => {
    this.changeCanvasFrameVisible(false);
    await new Promise<void>((resolve: () => void) => {
      this.setState(
        {
          isProjectLoaded: false,
          changeReason: ESdChangeReason.Loading,
          isProcessingWithSpinner: true
        },
        resolve
      );
    });
    try {
      return await delegate();
    } finally {
      // Deliberately NOT awaited: the caller waits for the work, never for the spinner
      // to finish clearing.
      this.setState(
        {
          isProjectLoaded: true,
          changeReason: ESdChangeReason.Loading,
          isProcessingWithSpinner: false
        },
        () => {
          this.changeCanvasFrameVisible(true);
        }
      );
    }
  };

  setSpinnerOn = () => {
    this.changeCanvasFrameVisible(false);
    this.setState({
      isProjectLoaded: false,
      changeReason: ESdChangeReason.Loading,
      isProcessingWithSpinner: true
    });
  };

  setSpinnerOff = () => {
    this.changeCanvasFrameVisible(true);
    this.setState({
      isProjectLoaded: true,
      changeReason: ESdChangeReason.Loading,
      isProcessingWithSpinner: false
    });
  };

  changeCanvasFrameVisible = (visible: boolean) => {
    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;

    if (!activeCanvasIFrame) return;

    // Never make the canvas iframe visible when the dashboard is in a non-design state
    // (e.g. branching, workflow, etc.) — the iframe is position:absolute at body level
    // and would overlay on top of the active canvas, displacing the toolbar.
    if (visible) {
      const ds = this.partial?.designSituationProvider?.state?.dashboardState;
      if (ds !== undefined && ds !== DashboardState.design && ds !== DashboardState.reportDesigner) {
        activeCanvasIFrame.dataset.visible = "false";
        return;
      }
    }

    if (!visible) {
      activeCanvasIFrame.dataset.visible = "false";
    } else {
      activeCanvasIFrame.dataset.visible = "true";
    }
  };

  fillProjects = async () => {
    const projects = await ProjectService.GetAllProjectNames();
    this.setProjects(projects.data);
  };

  async openProject(forceReFetchProject: boolean = false, callback = () => {}) {
    this.processWithSpinner(async () => {
      this.partial.screenProvider.resetInterval();
      const sdId: string = window.location.pathname.split("/").pop();
      const projectId: string = await ScreenService.getProjectIdFromScreenId(sdId);

      const [screenDesign, project] = await Promise.all([
        this.getScreenDesignFromServer(sdId),
        this.getProjectFromServer(projectId, forceReFetchProject)
      ]);

      if (!screenDesign || !project) {
        return;
      }
      // Replaced while the fetches were in flight (an app switch or Back before this load landed):
      // the newer provider owns the shared store now, so this load stops before touching it.
      if (!this.partial.isLive()) return;
      window.kuika.activeProjectId = project.id;
      window.kuika.activeProjectType = project.projectType;

      // KOA-18173: the departing screen's session must be flushed and LEFT before the new
      // tree is committed. Otherwise every save fired during the load window (component
      // migration, autosave) pairs the OLD session id with the NEW screen's payload and
      // corrupts the old row (name + publicJson + tree). The join for the new screen
      // stays after loadProject (post-migration seed).
      await this.partial.collabProvider.flushAndLeaveCollabSession();
      if (!this.partial.isLive()) return;
      this.partial.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.LoadProject);
      this.partial.branchingProvider.getBranches();
      // Designer-live (Faz 5): tool change events + project presence for the non-canvas tools.
      startDesignerLive(project.id);

      if (sdId) {
        const isSdReportPage = screenDesign.isAbsoluteDesign;
        if (isSdReportPage) {
          // A report opened from outside the tool — a reload on its URL, a chat or MCP link — opens
          // straight onto the report, whatever landing flag an earlier visit left behind. Inside the
          // tool nothing changes: a reload behind the user's back (AI edits, a branch switch) must
          // not close the list they are looking at.
          if (this.partial.designSituationProvider.state.dashboardState !== DashboardState.reportDesigner) {
            this.partial.designSituationProvider.enterReportDesigner({ showLanding: false });
          }
        } else if (this.partial.designSituationProvider.state.dashboardState === DashboardState.workFlow) {
          // KOA-17362 (F1/F2) — loading a real (non-report) screen means we are in
          // screen-design mode. The BPMN / Process-Automation module leaves
          // dashboardState=workFlow and nothing resets it on navigate, so every
          // subsequent screen edit ran under a stale workFlow state — and
          // saveActionToEvent routes an action attach by dashboardState, silently
          // writing screen action-attaches (StartProcess onClick, GetUserTasks
          // pageInit) to a workflow step instead of the screen. Restore the
          // design-mode invariant on screen load. Scoped to workFlow so branching /
          // configManager / notificationTemplate loads (which also run openProject)
          // are untouched.
          this.partial.designSituationProvider.setDashboardState(DashboardState.design);
        }
      }

      if (!this.state.projects || this.state.projects.length < 1) {
        this.fillProjects();
      }

      await this.loadProject(project, screenDesign);
      if (!this.partial.isLive()) return;

      // Real-time collaboration: the session join MUST come after loadProject, whose
      // component migration (migrateSdWithCurrentComponents) mutates the whole tree in
      // place and re-dispatches it. Joining earlier seeded the server with the
      // pre-migration tree while the shadow/live moved on — the accumulated drift then
      // erupted as a giant op batch on the user's first real edit (BAD_BATCH loop), and
      // on a second joiner the local re-dispatch silently clobbered the authoritative
      // snapshot. No-op while the flag is off.
      void this.partial.collabProvider.joinCollabSession(sdId);

      this.partial.customBlocksProvider.loadBlocks();

      if (this.partial.aiImageToScreenGenerator.aiResponseToBeGenerated) {
        await this.partial.aiImageToScreenGenerator.generateScreen();
      }

      if (localStorage.getItem("isWorkflowScreenStepWizardOpenByDefault") === "true") {
        this.partial.wizardProvider.setIsWorkflowScreenStepWizardOpen(true);
        localStorage.removeItem("isWorkflowScreenStepWizardOpenByDefault");
      }

      callback();
    });
  }

  public async getScreenListFromServer(projectId: string): Promise<IScreenDesignInfo[]> {
    let screenList: IScreenDesignInfo[] = [];
    const screenListDto = await (await ProjectService.GetScreenList(projectId)).data;
    screenList = KMainFunctions.convertScreenListDTOToInfoInterface(screenListDto);
    return screenList;
  }

  async getScreenDesignFromServer(sdId: string): Promise<IScreenDesign | undefined> {
    if (!sdId) {
      KMainFunctions.displayErrorNotification("Screen not found!");
      return;
    }
    let screenDesign = {} as IScreenDesign;
    let sdDTO: IScreenDesignDto | undefined;
    sdDTO = (await ScreenService.Get(sdId)).data as IScreenDesignDto;
    if (!sdDTO) {
      KMainFunctions.displayErrorNotification("Screen not loaded!");
      return;
    }
    screenDesign = KMainFunctions.convertSDDTOToIScreenDesignInterface(sdDTO);
    ScreenDesignHelper.preprocessScreen(screenDesign);
    if (sdDTO.inUseData?.isScreenInUse === true) {
      this.showSdInUseDetails(sdDTO.inUseData);
      return;
    }
    return screenDesign;
  }

  async getScreenDesignById(
    sdId: string,
    isLoadingAsMasterOfScreen: boolean = false
  ): Promise<IScreenDesign | undefined> {
    let screenDesign = {} as IScreenDesign;
    let sdDTO: IScreenDesignDto | undefined;
    sdDTO = (await ScreenService.Get(sdId, isLoadingAsMasterOfScreen)).data as IScreenDesignDto;
    if (!sdDTO) {
      KMainFunctions.displayErrorNotification("Screen not loaded!");
      return;
    }
    screenDesign = KMainFunctions.convertSDDTOToIScreenDesignInterface(sdDTO);
    ScreenDesignHelper.preprocessScreen(screenDesign);
    if (!isLoadingAsMasterOfScreen && sdDTO.inUseData?.isScreenInUse === true) {
      this.showSdInUseDetails(sdDTO.inUseData);
      return;
    }
    return screenDesign;
  }

  /**
   * Designer-live refresh (Faz 6.1): fetch the project fresh and WRITE it into state —
   * getProjectFromServer alone only returns the value, which made the project.changed
   * reload inert. prevProjectRevision must move together with project: it is
   * updateProject's diff baseline, and leaving it stale would make the next local
   * patch re-send every remotely-changed field. The raw fetch is not state-shape
   * complete — see project-refresh-enrichment.ts for the guard + graft contract.
   */
  refreshProjectFromServer = async (projectId: string): Promise<void> => {
    if (!projectId) return;
    const current: IProject = this.state.project;
    if (!isProjectRefreshSafe(current)) return;
    const project: IProject | undefined = await this.getProjectFromServer(projectId, true);
    if (project === undefined || _.isEmpty(project)) return;
    graftClientProjectEnrichments(project, current);
    this.setState({
      project,
      prevProjectRevision: _.cloneDeep(project),
      changeReason: ESdChangeReason.OtherSetStates
    });
  };

  async getProjectFromServer(projectId: string, force: boolean = false): Promise<IProject | undefined> {
    let project: IProject | undefined = this.state.project;
    if (force || project === undefined || _.isEmpty(project)) {
      const projectData = (await (await ProjectService.Get(projectId)).data) as IProjectDto;
      if (!projectData?.managedDbExists) {
        this.handleChangeVisibilityOfAccessLimitedModal(true);
      }
      project = KMainFunctions.convertProjectDTOToInterface(projectData);
    }
    if (project === undefined || _.isEmpty(project)) {
      KMainFunctions.displayErrorNotification("Application not loaded!");
      return;
    }
    return project;
  }

  async loadNotificationTemplates(project: IProject) {
    try {
      this.setState({ isNotificationTemplatesLoading: true });
      // TODO: Bu endpointin project id'ye ihtiyac duymamasi gerekiyor.
      const notificationTemplatesResponse = await NotificationTemplateService.GetNotificationTemplates(project.id);

      if (notificationTemplatesResponse.status === 200) {
        this.setState({
          notificationTemplates: notificationTemplatesResponse.data
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isNotificationTemplatesLoading: false });
    }
  }

  async loadProject(project: IProject, screenDesign: IScreenDesign) {
    const screenList: IScreenDesignInfo[] = await this.getScreenListFromServer(screenDesign.projectId);
    if (window?.kuika) {
      window.kuika.activeProjectId = project?.id;
      window.kuika.activeProjectName = project?.name;
      window.kuika.activeProjectType = project?.projectType;
      window.dispatchEvent(new CustomEvent("kuika-project-loaded", { detail: { projectId: project?.id } }));
    }

    await this.checkForMicroAppsAndLoadScreens(screenDesign);
    let autoSaveDelay = this.state.autosaveDelay;
    if (_.isEmpty(project.componentSetObj?.components)) {
      const [compSetObj, compsDTO] = await Promise.all([
        ComponentSetService.Get(Guid.EMPTY),
        ComponentService.GetAllByComponentSetId(Guid.EMPTY)
      ]);

      const iCompSet = CommonHelperFunctions.convertComponentSetDTOToInterface(compSetObj.data);
      const iComps = CommonHelperFunctions.convertComponentDTOToInterface(compsDTO.data as IComponentDto[]);
      const componentSetJson = JSON.parse(compSetObj.data.json);
      autoSaveDelay = componentSetJson.autosaveDelay;
      iCompSet.components = iComps;
      project.componentSetObj = iCompSet;
    }
    if (!project.languages || _.isEmpty(project.languages)) {
      if (!project.languages) {
        project.languages = [];
      }
      const lang = {
        id: Guid.create().toString(),
        name: "en_US",
        shortName: "English",
        isDefault: true
      };
      project.languages.push(lang);
    }

    // Fetch widget projects - always fetch to ensure domain types are available
    // for both existing widgets and newly dropped widgets
    let widgetProjects: WidgetProjectInfo[] = [];
    try {
      const widgetResponse = await WidgetsService.GetWidgetProjects();
      widgetProjects = widgetResponse?.data || [];
      // Register widget domain types for symbol picker access
      this.registerWidgetDomainTypes(widgetProjects);
    } catch {
      // If fetching widgets fails, continue without widget domain types
    }

    // renewComponent mutates displayItems in place to upgrade them to the current component
    // schema (new default properties, renamed component types). Detect whether it actually
    // changed anything so we can persist that upgrade immediately as a system-origin write
    // (see the ComponentsMigrated save below) instead of letting the lazy 5-min autosave pick
    // it up mixed with the user's first real edit.
    const displayItemsBeforeMigration: string = jsonStringifySafe(screenDesign.displayItems);
    this.migrateSdWithCurrentComponents(screenDesign.displayItems, project.componentSetObj.components, widgetProjects);
    // KOA-18016: heal StaticTables whose rows contain an element orphaned outside any StaticTableColumn
    // (which makes the table's columns unselectable / undroppable). Runs inside the migration diff
    // window so a repair is captured by didMigrateComponents below and persisted via the same
    // ComponentsMigrated save — existing broken reports are fixed on first load, healthy ones untouched.
    StaticTableHelper.normalizeStaticTableRows(screenDesign);
    const didMigrateComponents: boolean = displayItemsBeforeMigration !== jsonStringifySafe(screenDesign.displayItems);
    this.loadNotificationTemplates(project);

    let masterSd: IScreenDesign | undefined;
    const masterSdId = this.partial.screenProvider.getMasterScreenIdById(screenDesign?.id, screenList);
    if (masterSdId) {
      masterSd = await this.getScreenDesignById(masterSdId, true);
    }

    if (project !== undefined && screenDesign !== undefined) {
      this.setState(
        {
          sdId: screenDesign?.id,
          prevProjectRevision: _.cloneDeep(project),
          project,
          lastSavedScreenDIsString: jsonStringifySafe(this.partial.screenProvider.getScreenDesign()?.displayItems),
          sdName: screenDesign?.name,
          autosaveDelay: autoSaveDelay,
          screenList,
          selectedKuikaIds: [SpecialDIIds.rootDI.toString()],
          isAlternativeSdModalActive: false,
          currentScreenOccupier: undefined,
          occupiedScreenName: undefined,
          masterSd
        },
        () => {
          this.partial.configManagerProvider.fetchConfigs({
            shouldLockPage: false
          });

          if (screenDesign !== undefined) {
            this.partial.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.LoadProject);
            this.partial.designTimeElementStateProvider.initializeFromScreenDesign();
            if (didMigrateComponents) {
              // Persist the on-load schema upgrade right now, before the user touches anything,
              // tagged ComponentsMigrated so the backing save sends X-Kuika-Write-Origin. In a
              // branch this is applied to the snapshot silently (no staged change / no conflict);
              // outside branching it is a normal, immediate persist. Best-effort: a failure just
              // defers to the next autosave.
              void this.partial.screenProvider
                .saveScreen(true, undefined, true, ESdChangeReason.ComponentsMigrated)
                .catch(() => undefined);
            }
          }
        }
      );
    }

    const hasWebScreen = this.state.screenList?.find((x) => x.publicMetaData?.screenType === EScreenType.WebScreen);
    const isNavigatingToOfflineScreen = project.offlineScreenId === screenDesign.id;
    const wasOnOfflineScreen =
      this.state.project?.offlineScreenId && this.state.project.offlineScreenId === this.state.sdId;
    const offlineStatusChanged = isNavigatingToOfflineScreen || wasOnOfflineScreen;
    if (_.isEmpty(this.state.actionDefinitions) || hasWebScreen || offlineStatusChanged) {
      this.loadActionDefinitions();
    }
    // TODO:TURENC --> WorkFlow sekmesine geçildiğinde çekilmeli!
    await this.partial.workflowProvider.loadWorkFlowList(project.id);
    const lastWf = this.partial.workflowProvider.getProviderValues().lastOpenWfId;
    if (lastWf && this.partial.designSituationProvider.state.dashboardState === DashboardState.workFlow)
      this.partial.workflowProvider.setSelectedWf(lastWf);
    this.partial.workflowProvider.setIsWorkFlowWindowReload();
    await this.fetchWhatsappBusinessTemplates();
  }

  changeScreenListOrder = (screenList: IScreenDesignListDto[], a: any, b: any) => {
    // let temp = screenList[a];
    // screenList[a] = screenList[b];
    // screenList[b] = temp;
    [screenList[a], screenList[b]] = [screenList[b], screenList[a]];
    this.setState({ screenList });
  };

  showSdInUseDetails(sdInUseData: IScreenDesignInUseData) {
    KMainFunctions.displayWarningNotification(
      `Screen [${sdInUseData.screenName}] is already in use by [${sdInUseData.usingBy}].`
    );
    if (this.state.project?.id) {
      // I'm already in a screen
      this.props.navigator.gotoSd(this.state.sdId); // Convert Browser's url to old sd id
    } else if (sdInUseData.alternativeSdList.length === 0) {
      KMainFunctions.displayWarningNotification("There is no alternative screen to use, redirecting to projects page.");
      this.props.navigator.gotoProjects();
    } else if (sdInUseData.alternativeSdList.length === 1) {
      KMainFunctions.displayWarningNotification(
        `There is 1 alternative screen to use (${sdInUseData.alternativeSdList[0].name}), redirecting to this screen.`
      );
      this.partial.screenProvider.loadScreen(sdInUseData.alternativeSdList[0].id);
    } else {
      this.setState({
        isAlternativeSdModalActive: true,
        currentScreenOccupier: sdInUseData.usingBy,
        occupiedScreenName: sdInUseData.screenName,
        alternativeSdList: sdInUseData.alternativeSdList
      });
    }
    if (this.state.dashboardState === DashboardState.reportDesigner) {
      this.setState({ isCreateReportDesignPageActive: true });
    }
  }

  migrateSdWithCurrentComponents = (
    DIs: IDisplayItem[],
    components: IComponent[],
    widgetProjects: WidgetProjectInfo[] = []
  ): IDisplayItem[] => {
    if (!DIs) {
      return [];
    }
    DIs.map((x) => {
      return this.updateComponentsTree(x, components, widgetProjects);
    });
    return DIs;
  };

  updateComponentsTree = (
    di: IDisplayItem,
    components: IComponent[],
    widgetProjects: WidgetProjectInfo[] = []
  ): IDisplayItem => {
    const queue = [di];

    while (queue.length > 0) {
      const currentDi = queue.shift()!;

      if (currentDi.item) {
        this.renewComponent(currentDi.item, components, widgetProjects);
      }

      if (currentDi.children?.length > 0) {
        queue.push(...currentDi.children);
      }
    }

    return di;
  };

  renewComponent = (
    oldComp: IDisplayItemComponent,
    components: IComponent[],
    widgetProjects: WidgetProjectInfo[] = []
  ) => {
    const originalComp = ScreenDesignHelper.findComponentById(oldComp.id, components);

    if (originalComp?.isHtml === true) {
      return;
    }

    if (originalComp === undefined) {
      const DIs = this.partial.screenProvider.getScreenDesign().displayItems;
      if (!DIs) {
        return;
      }
      DIs.forEach((x) => {
        this.deleteComponentFromTree(x, oldComp.id);
      });
    } else {
      oldComp.name = originalComp.name;

      // 1.Update old component's properties
      const propsToDelete = [] as IDisplayItemProperty[];

      oldComp.properties.forEach((oldProp) => {
        const newProp = originalComp?.properties.find((x) => x.name === oldProp.name);
        if (newProp === undefined) {
          if (oldComp.name === "MicroApp" && oldProp?.name?.startsWith("screenInput_")) {
            return;
          }
          if (oldProp.isDynamicProperty === true) {
            return;
          }
          propsToDelete.push(oldProp);
        }
      });

      propsToDelete.forEach((x) => {
        oldComp.properties = oldComp.properties.filter((y) => y.name !== x.name);
      });

      // 2. Add new properties to old component
      originalComp.properties.forEach((newProp) => {
        if (!oldComp.properties.some((y) => newProp.name === y.name)) {
          oldComp.properties.push({
            name: newProp.name,
            symbol: newProp.symbol,
            selectedType: newProp.selectedType
          });
        }
      });

      // 3. Migrate widget dynamic properties if this is a Widget Renderer
      if (originalComp.name === "Widget Renderer" && widgetProjects.length > 0) {
        this.migrateWidgetDynamicProperties(oldComp, widgetProjects);
      }
    }
  };

  /**
   * Checks if there are any Widget Renderer components in the display items tree.
   * Used to skip widget fetching if no widgets are present in the screen.
   */
  private hasWidgetRendererInDisplayItems = (displayItems: IDisplayItem[]): boolean => {
    if (!displayItems || displayItems.length === 0) {
      return false;
    }

    const queue = [...displayItems];
    while (queue.length > 0) {
      const di = queue.shift()!;
      if (di.item?.name === "Widget Renderer") {
        return true;
      }
      if (di.children?.length > 0) {
        queue.push(...di.children);
      }
    }
    return false;
  };

  /**
   * Migrates dynamic properties for a Widget Renderer component based on the current widget definition.
   * - Removes dynamic properties that no longer exist in the widget definition
   * - Adds new dynamic properties from the widget definition
   * - Preserves existing dynamic properties that still exist
   */
  private migrateWidgetDynamicProperties = (component: IDisplayItemComponent, widgetProjects: WidgetProjectInfo[]) => {
    // Get packId and componentId from component properties
    const packIdProp = component.properties.find((p) => p.name === "packId");
    const componentIdProp = component.properties.find((p) => p.name === "componentId");

    const packId = packIdProp?.symbol?.fixedValue as string;
    const componentId = componentIdProp?.symbol?.fixedValue as string;

    if (!packId || !componentId) {
      return;
    }

    // Find the widget project and component
    const widgetProject = widgetProjects.find((p) => p.projectId === packId);
    if (!widgetProject) {
      return;
    }

    const widgetComponent = widgetProject.components.find((c) => c.widgetId === componentId);
    if (!widgetComponent) {
      return;
    }

    const resolvedProps = widgetComponent.resolvedProps || [];

    // Get current dynamic properties
    const currentDynamicProps = component.properties.filter((p) => p.isDynamicProperty === true);

    // Create a set of new prop names for quick lookup
    const newPropNames = new Set(resolvedProps.map((p) => p.propName));

    // Remove dynamic properties that no longer exist in the widget definition
    const dynamicPropsToRemove = currentDynamicProps.filter((p) => !newPropNames.has(p.name));
    dynamicPropsToRemove.forEach((prop) => {
      component.properties = component.properties.filter((p) => p.name !== prop.name);
    });

    // Update existing dynamic properties if their definitions changed
    const existingPropNames = new Set(component.properties.map((p) => p.name));
    resolvedProps.forEach((resolvedProp) => {
      if (existingPropNames.has(resolvedProp.propName)) {
        // Property exists - check if we need to update its definition
        const existingProp = component.properties.find((p) => p.name === resolvedProp.propName);
        if (existingProp?.isDynamicProperty) {
          const newSelectedType = this.mapResolvedPropToPropertyType(resolvedProp);
          const newDefinition = {
            name: resolvedProp.propName,
            type: [newSelectedType],
            options: resolvedProp.allowedValues ?? [],
            hasOptions: (resolvedProp.allowedValues?.length ?? 0) > 0,
            description: "",
            typeOptions: [],
            isRequired: resolvedProp.isRequired,
            isTechnical: false,
            isAdvanced: false,
            isIgnoredOnCodeOutput: false,
            isIgnoredOnGeneratedApp: false,
            tagName: "",
            defaultValue: String(this.getDefaultValueForWidgetProp(resolvedProp) ?? ""),
            enumType: this.mapKuikaTypeNameToEnumType(resolvedProp),
            kuikaTypeName: resolvedProp.kuikaTypeName,
            isDomainType: resolvedProp.isDomainType,
            domainTypeName: resolvedProp.domainTypeName,
            isList: resolvedProp.isList
          };

          // Update the property definition and type (preserve the user's symbol value)
          existingProp.selectedType = newSelectedType;
          existingProp.dynamicPropertyDefinition = newDefinition;
        }
      } else {
        // Property doesn't exist - add it
        const newProp = this.createWidgetDynamicProperty(resolvedProp);
        component.properties.push(newProp);
      }
    });
  };

  /**
   * Creates a dynamic property from a resolved widget prop.
   */
  private createWidgetDynamicProperty = (prop: IKuikaTypeResolution): IDisplayItemProperty => {
    const selectedType = this.mapResolvedPropToPropertyType(prop);
    const symbol: ISymbol = {
      category: ESymbolCategory.fixed,
      label: prop.propName,
      description: "",
      fixedValue: this.getDefaultValueForWidgetProp(prop),
      isFixed: true
    };

    return {
      name: prop.propName,
      symbol,
      selectedType,
      isDynamicProperty: true,
      dynamicPropertyDefinition: {
        name: prop.propName,
        type: [selectedType],
        options: prop.allowedValues ?? [],
        hasOptions: (prop.allowedValues?.length ?? 0) > 0,
        description: "",
        typeOptions: [],
        isRequired: prop.isRequired,
        isTechnical: false,
        isAdvanced: false,
        isIgnoredOnCodeOutput: false,
        isIgnoredOnGeneratedApp: false,
        tagName: "",
        defaultValue: String(this.getDefaultValueForWidgetProp(prop) ?? ""),
        enumType: this.mapKuikaTypeNameToEnumType(prop),
        kuikaTypeName: prop.kuikaTypeName,
        isDomainType: prop.isDomainType,
        domainTypeName: prop.domainTypeName,
        isList: prop.isList
      }
    };
  };

  private mapKuikaTypeNameToEnumType = (prop: IKuikaTypeResolution): EEnumType => {
    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return EEnumType.string;
      case "double":
      case "number":
        return EEnumType.number;
      case "boolean":
        return EEnumType.boolean;
      default:
        return EEnumType.object;
    }
  };

  /**
   * Maps a resolved prop to the corresponding EPropertyType.
   */
  private mapResolvedPropToPropertyType = (prop: IKuikaTypeResolution): EPropertyType => {
    if (prop.isEvent) {
      return EPropertyType.event;
    }

    if (prop.propertyType && prop.propertyType !== EPropertyType.event) {
      return prop.propertyType as EPropertyType;
    }

    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return EPropertyType.string;
      case "double":
      case "number":
        return EPropertyType.number;
      case "boolean":
        return EPropertyType.boolean;
      case "datetime":
        return EPropertyType.dateTime;
      default:
        if (prop.isDomainType) {
          return EPropertyType.object;
        }
        if (prop.allowedValues && prop.allowedValues.length > 0) {
          return EPropertyType.enum;
        }
        return EPropertyType.string;
    }
  };

  /**
   * Gets the default value for a widget property based on its type.
   */
  private getDefaultValueForWidgetProp = (prop: IKuikaTypeResolution): unknown => {
    if (prop.isList) {
      return [];
    }

    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return "";
      case "double":
      case "number":
        return 0;
      case "boolean":
        return false;
      default:
        if (prop.allowedValues && prop.allowedValues.length > 0) {
          return prop.allowedValues[0];
        }
        return null;
    }
  };

  deleteComponentFromTree = (di: IDisplayItem, compIdToDelete: string): IDisplayItem => {
    const queue = [di];

    while (queue.length > 0) {
      const currentDi = queue.shift()!;

      if (currentDi.children) {
        currentDi.children = currentDi.children.filter((x) => x.item.id !== compIdToDelete);
        queue.push(...currentDi.children);
      }
    }

    return di;
  };

  setNeededComponentsForThumbnail = (comp: IComponent, compList: IComponent[]): IComponent[] => {
    const comps: IComponent[] = [];
    comps.push(comp);
    if (comp.childrenInfo && comp.childrenInfo.length > 0) {
      comp.childrenInfo.forEach((cName) => {
        const child = compList.find((x) => x.name === cName.name);
        if (child) {
          comps.push(child);
        }
      });
    }
    return comps;
  };

  setDatasourceValidationErrors = (errors: string[]) => {
    this.setState({ datasourceValidationErrors: errors }, () => {
      if (errors.length > 0) {
        this.partial.designSituationProvider.setWarningPanelType(WarningPanelType.Open);
      }
    });
  };

  setNewScreenModalVisibility = (value: boolean) => {
    this.setState({ isNewScreenModalVisible: value });
  };

  setNewFolderModalVisibility = (value: boolean) => {
    this.setState({ isNewFolderModalVisible: value });
  };

  setPendingScreenFolderTargetId = (folderId: string | null) => {
    this.setState({ pendingScreenFolderTargetId: folderId });
  };

  setPendingNewScreenType = (screenType: EScreenType | null) => {
    this.setState({ pendingNewScreenType: screenType });
  };

  handleSetPreviewGenerationStatus = (isGeneratingPreview: boolean) => {
    this.setState({ isGeneratingPreview });
  };

  setSearchTexts = (panelKey: ViewPart, searchText: string) => {
    const searchTexts = {
      ...this.state.searchTexts,
      [panelKey]: searchText
    };
    this.setState({ searchTexts });
  };

  handleChangeVisibilityOfAccessLimitedModal = (showAccessLimitedModal: boolean = false) => {
    this.setState({ showAccessLimitedModal });
  };

  fetchWhatsappBusinessTemplates = async (): Promise<void> => {
    // The templates are per project. Global Configs saves call this with no project open, and
    // asking for `project/undefined/whatsapp` only earns the user a 400 toast.
    const projectId: string | undefined = this.state.project?.id;
    if (!projectId) return;
    try {
      const templates = await ConfigService.getAllWhatsappBusinessTemplates(projectId);
      if (templates.status === 200) {
        this.setState({ whatsappBusinessTemplates: templates.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };
}
